/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { PositionType, UitkMenu, UitkMenuContainer, UitkMenuList, UitkMenuTrigger } from 'uitk-react-menu';
import { CopyAction } from '../copy-action/copy-action';
import { UitkText } from 'uitk-react-text';
import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkSecondaryButton } from 'uitk-react-button';
import useAnalytics from './../../hooks/analytics/useAnalytics';
import { AuthenticationLinkFragment } from './../../graphql/__generated__/types';
import { useLocation } from 'react-router-dom';

interface AuthenticatedUserMenuProps {
  menu: any;
}

export const AuthenticatedUserMenu: FC<AuthenticatedUserMenuProps> = ({ menu }) => {
  const { subMenus } = menu;
  const { trackSignOutSubmitted } = useAnalytics();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const buttonOnClickHandler = (action: AuthenticationLinkFragment) => {
    const target = action.target === 'EXTERNAL' ? '_blank' : '_self';
    if (target === '_self') {
      trackSignOutSubmitted();
    }
    const targetURI = action.resource.value;
    return window.open(targetURI, target);
  };

  const buttonsAsMenuItems = menu.buttons.map((button, index) => ({
    label: button.primary,
    className: `uitk-list-item ${index < menu.buttons.length - 1 ? 'authenticated-menu-item' : ''}`,
    onClick: () => buttonOnClickHandler(button.action),
    icon: { name: button.icon?.token },
  }));

  const handleAuthenticatedUserMenuClick = () => {
    setOpen(!open);
  };

  const currentPartnerName = subMenus[0].items[0].title;
  const currentPartnerType = subMenus[0].items[1].title;
  const currentPartnerId = subMenus[0].items[2].title;

  const partnerSwitchMenu = subMenus[1];

  const partnerSwitchMenuItems = partnerSwitchMenu.items.map((item) => {
    return {
      label: item.title,
      className: 'uitk-list-item',
      onClick: () => partnerSwitch(item.action.resource.value),
    };
  });

  const authorizeUrl = '/eg/auth/custom/authorize';

  const getPartnerAccountId = (authUrl: string): string => {
    const partnerSearchParam = 'assumed_partner_account_id=';
    return authUrl.substring(authUrl.indexOf(partnerSearchParam) + partnerSearchParam.length);
  };

  const partnerSwitch = (authUrl: string): void => {
    const partnerId = getPartnerAccountId(authUrl);
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('redirect_uri', location.pathname);
    urlSearchParams.append('assumed_partner_account_id', partnerId);
    window.location.href = `${authorizeUrl}?${urlSearchParams.toString()}`;
  };

  return (
    <>
      <UitkMenu test-id="authenticated-user-menu" isOpen={open} onTriggerClick={handleAuthenticatedUserMenuClick}>
        <UitkMenuTrigger>
          <UitkSecondaryButton>
            <UitkIcon name={menu.icon.token} size={UitkIconSize.XLARGE} />
            {menu.title}
          </UitkSecondaryButton>
        </UitkMenuTrigger>
        <UitkMenuContainer position={PositionType.RIGHT} width={420} className={'authenticated-menu-container'}>
          <UitkCard>
            <UitkCardContentSection className="user-info-section">
              <UitkText theme="default" size={400} weight="medium" spacing="one">
                {currentPartnerName}
              </UitkText>
              <UitkText theme="default" spacing="one">
                {currentPartnerType}
              </UitkText>
              <UitkLayoutFlex justifyContent="space-between" alignItems="center">
                <UitkLayoutFlexItem>
                  <UitkText theme="default">{currentPartnerId}</UitkText>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <CopyAction copyValue={currentPartnerId.split(': ')[1]} />
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkCardContentSection>
            {partnerSwitchMenu && (
              <UitkCardContentSection border="top" className="partner-selection-menu-section">
                <UitkText theme="default" size={400} weight="medium" spacing="one">
                  {partnerSwitchMenu.title}
                </UitkText>
                <UitkMenuList items={partnerSwitchMenuItems} />
              </UitkCardContentSection>
            )}
            <UitkCardContentSection border="top" className="authenticated-menu-buttons-section">
              <UitkMenuList items={buttonsAsMenuItems} />
            </UitkCardContentSection>
          </UitkCard>
        </UitkMenuContainer>
      </UitkMenu>
    </>
  );
};
